/**
 * useAlertStream — live alert WebSocket hook.
 *
 * Connects to Crowd API /events namespace (Socket.IO). Falls back to the
 * simulation WS after PRIMARY_FAIL_THRESHOLD connect failures.
 *
 * Listens for:
 *   alert_new          — new Alert object (WSMessage envelope or raw)
 *   alert_state_change — updated Alert after ack / escalate / resolve
 *
 * acknowledge / escalate / resolve POST to the approved alert lifecycle facade.
 * The live hook applies only the returned authoritative Alert state.
 *
 * No PII. All alert payloads reference zone names and density aggregates.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { Alert } from '@vigilo/shared';
import { CROWD_SIMULATION_RESET_EVENT } from './useSimulation';
import { get, post } from '@vigilo/api-client';

const PRIMARY_WS_URL = (import.meta.env['VITE_CROWD_WS_URL'] as string | undefined)
  ?? (import.meta.env['VITE_API_GATEWAY_WS_URL'] as string | undefined)
  ?? (import.meta.env['VITE_CORE_WS_URL'] as string | undefined)
  ?? 'http://localhost:3011';
const SIM_WS_URL = (import.meta.env['VITE_SIM_WS_URL'] as string | undefined)
  ?? 'http://localhost:3002';
const PRIMARY_SOCKET_PATH = (import.meta.env['VITE_CROWD_SOCKET_PATH'] as string | undefined)
  ?? (import.meta.env['VITE_API_GATEWAY_SOCKET_PATH'] as string | undefined)
  ?? (import.meta.env['VITE_CORE_SOCKET_PATH'] as string | undefined);
const SIM_SOCKET_PATH = import.meta.env['VITE_SIM_SOCKET_PATH'] as string | undefined;

const PRIMARY_FAIL_THRESHOLD = 3;
// The socket is best-effort; REST is the source of truth for the list and the summary. Re-reading it
// on a short interval keeps the badge, top bar and KPIs correct even when no socket event arrives.
const ALERT_RESYNC_MS = 2000; // same 2 s cadence as the other live hooks
const CROWD_ALERT_ESCALATION_REASON = 'Command review requested';

/**
 * Socket.IO reads the path of the URL given to io() as the *namespace*. A gateway-relative base such
 * as "/crowd-api" (the all-in-one build) would ask for the namespace "/crowd-api/events", which the
 * server rejects with "Invalid namespace" - so no alert event would ever arrive. Keep only the origin
 * here; the gateway prefix belongs in the socket `path` option (PRIMARY_SOCKET_PATH).
 */
function eventsNamespaceUrl(base: string): string {
  return `${new URL(base, window.location.origin).origin}/events`;
}

type AlertLifecycleResponse = Alert | { data?: Alert };
export type AlertStreamSource = 'core' | 'simulation' | 'mock' | 'none';
export type AlertStreamConnectionStatus =
  | 'disabled'
  | 'requires_operation'
  | 'connecting'
  | 'connected'
  | 'fallback'
  | 'stale'
  | 'disconnected';

export interface AlertStreamSummary {
  total: number;
  active: number;
  open: number;
  acknowledged: number;
  investigating: number;
  contained: number;
  escalated: number;
  resolved: number;
}

interface AlertListResponse {
  data?: Alert[];
  summary?: AlertStreamSummary;
}

export interface AlertStreamState {
  alerts:           Alert[];
  summary:          AlertStreamSummary;
  connected:        boolean;
  connectionStatus: AlertStreamConnectionStatus;
  source:           AlertStreamSource;
  lastConnectedAt:  string | null;
  lastEventAt:      string | null;
  lastSyncAt:       string | null;
  error:            string | null;
  acknowledge:      (alertId: string) => Promise<void>;
  escalate:         (alertId: string) => Promise<void>;
  resolve:          (alertId: string, note: string) => Promise<void>;
}

export interface AlertStreamOptions {
  enabled?: boolean;
  requireOperation?: boolean;
  zoneIds?: readonly string[];
}

export const EMPTY_ALERT_STREAM_SUMMARY: AlertStreamSummary = {
  total: 0,
  active: 0,
  open: 0,
  acknowledged: 0,
  investigating: 0,
  contained: 0,
  escalated: 0,
  resolved: 0,
};

function unwrapAlertResponse(body: AlertLifecycleResponse): Alert | null {
  if (!body || typeof body !== 'object') return null;
  const candidate = 'data' in body ? body.data : body;
  return candidate && typeof candidate === 'object' && 'id' in candidate ? candidate : null;
}

function summaryFromAlerts(alerts: Alert[]): AlertStreamSummary {
  const open = alerts.filter(alert => alert.status === 'open').length;
  const acknowledged = alerts.filter(alert => alert.status === 'acknowledged').length;
  const investigating = alerts.filter(alert => alert.status === 'investigating').length;
  const contained = alerts.filter(alert => alert.status === 'contained').length;
  const escalated = alerts.filter(alert => alert.status === 'escalated').length;
  const resolved = alerts.filter(alert => alert.status === 'resolved').length;
  return {
    total: alerts.length,
    active: open + acknowledged + investigating + contained + escalated,
    open,
    acknowledged,
    investigating,
    contained,
    escalated,
    resolved,
  };
}

function unwrapAlertListResponse(body: unknown): { alerts: Alert[]; summary: AlertStreamSummary } {
  if (Array.isArray(body)) {
    const alerts = body as Alert[];
    return { alerts, summary: summaryFromAlerts(alerts) };
  }
  const direct = body as AlertListResponse;
  const alerts = Array.isArray(direct?.data) ? direct.data : [];
  return {
    alerts,
    summary: direct?.summary ?? summaryFromAlerts(alerts),
  };
}

function adjustStatus(summary: AlertStreamSummary, status: Alert['status'], delta: number): AlertStreamSummary {
  const next = { ...summary };
  next.total = Math.max(0, next.total + delta);
  if (status !== 'resolved') next.active = Math.max(0, next.active + delta);
  next[status] = Math.max(0, next[status] + delta);
  return next;
}

function updateSummary(
  summary: AlertStreamSummary,
  previous: Alert | null,
  current: Alert,
  countWhenMissing: boolean,
): AlertStreamSummary {
  if (!previous) return countWhenMissing ? adjustStatus(summary, current.status, 1) : summary;
  if (previous.status === current.status) return summary;
  return adjustStatus(adjustStatus(summary, previous.status, -1), current.status, 1);
}

export function useAlertStream(operationId?: string | null, options: AlertStreamOptions = {}): AlertStreamState {
  const [alerts,    setAlerts]    = useState<Alert[]>([]);
  const [summary, setSummary] = useState<AlertStreamSummary>(EMPTY_ALERT_STREAM_SUMMARY);
  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<AlertStreamConnectionStatus>('connecting');
  const [source, setSource] = useState<AlertStreamSource>('none');
  const [lastConnectedAt, setLastConnectedAt] = useState<string | null>(null);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const enabled = options.enabled ?? true;
  const requireOperation = options.requireOperation ?? false;
  const zoneIdsKey = (options.zoneIds ?? []).join(',');

  const socketRef = useRef<Socket | null>(null);
  const lastSyncAtRef = useRef<string | null>(null);
  const alertsRef = useRef<Alert[]>([]);
  const zoneIdsRef = useRef<Set<string> | null>(null);
  // Wall-clock time of the last socket event or lifecycle action applied locally. A resync whose
  // request started before it must not overwrite that newer state with an older snapshot.
  const lastLocalChangeAtRef = useRef(0);

  useEffect(() => {
    zoneIdsRef.current = zoneIdsKey
      ? new Set(zoneIdsKey.split(',').map(zoneId => zoneId.trim()).filter(Boolean))
      : null;
  }, [zoneIdsKey]);

  // Inserts a new alert at the front (newest first) or replaces if id exists.
  const addOrUpdate = useCallback((alert: Alert, countWhenMissing = false) => {
    if (operationId && alert.operationId !== operationId) return;
    if (zoneIdsRef.current && !zoneIdsRef.current.has(alert.zoneId)) return;
    const previous = alertsRef.current.find(a => a.id === alert.id) ?? null;
    lastLocalChangeAtRef.current = Date.now();
    setAlerts(prev => {
      const idx = prev.findIndex(a => a.id === alert.id);
      if (idx === -1) {
        const next = [alert, ...prev];
        alertsRef.current = next;
        return next;
      }
      const next = [...prev];
      next[idx] = alert;
      alertsRef.current = next;
      return next;
    });
    setSummary(prev => updateSummary(prev, previous, alert, countWhenMissing));
  }, [operationId]);

  useEffect(() => {
    let active        = true;
    let failCount     = 0;
    let usingFallback = false;
    // resync() is only usable once the seed request path and socket exist; the reset handler below can
    // fire earlier (e.g. while the hook is disabled), so it is gated on this flag.
    let resyncEnabled = false;
    let resyncInFlight = false;
    function onSimulationReset() {
      setAlerts([]);
      alertsRef.current = [];
      setSummary(EMPTY_ALERT_STREAM_SUMMARY);
      setLastEventAt(null);
      setLastSyncAt(null);
      lastLocalChangeAtRef.current = Date.now();
      // Repopulate from REST right away instead of waiting for the next socket event.
      void resync();
    }
    window.addEventListener(CROWD_SIMULATION_RESET_EVENT, onSimulationReset);
    if (!enabled || (requireOperation && !operationId)) {
      setAlerts([]);
      alertsRef.current = [];
      setSummary(EMPTY_ALERT_STREAM_SUMMARY);
      setConnected(false);
      setConnectionStatus(!enabled ? 'disabled' : 'requires_operation');
      setSource('none');
      setLastConnectedAt(null);
      setLastEventAt(null);
      setLastSyncAt(null);
      lastSyncAtRef.current = null;
      setError(null);
      socketRef.current?.disconnect();
      return () => {
        active = false;
        window.removeEventListener(CROWD_SIMULATION_RESET_EVENT, onSimulationReset);
      };
    }

        const alertParams = new URLSearchParams();
        if (operationId) alertParams.set('operationId', operationId);
        if (zoneIdsKey) alertParams.set('zoneIds', zoneIdsKey);
        alertParams.set('page', '1');
        alertParams.set('pageSize', '100');
        const alertPath = `/alerts${alertParams.toString() ? `?${alertParams.toString()}` : ''}`;

    setAlerts([]);
    alertsRef.current = [];
    setSummary(EMPTY_ALERT_STREAM_SUMMARY);
    setConnected(false);
    setConnectionStatus('connecting');
    setSource('core');
    setLastConnectedAt(null);
    setLastEventAt(null);
    setLastSyncAt(null);
    lastSyncAtRef.current = null;
    setError(null);

    // Normalise both WSMessage<Alert> envelope and bare Alert payloads.
    function parseAlert(msg: unknown): Alert | null {
      if (!msg || typeof msg !== 'object') return null;
      const m = msg as Record<string, unknown>;
      const a = (m['payload'] ?? m) as Alert;
      return a?.id ? a : null;
    }

    function markSynced(eventReceived: boolean) {
      const now = new Date().toISOString();
      if (eventReceived) setLastEventAt(now);
      setLastSyncAt(now);
      lastSyncAtRef.current = now;
    }

    function onAlertNew(msg: unknown) {
      if (!active) return;
      const a = parseAlert(msg);
      if (a) {
        markSynced(true);
        addOrUpdate(a, true);
      }
    }

    function onAlertStateChange(msg: unknown) {
      if (!active) return;
      const a = parseAlert(msg);
      if (a) {
        markSynced(true);
        addOrUpdate(a);
      }
    }

    function connectSimulation() {
      if (!active || usingFallback) return;
      usingFallback = true;
      socketRef.current?.disconnect();
      setConnectionStatus('connecting');
      setSource('simulation');
      const sim = io(SIM_WS_URL, { reconnection: true, timeout: 8000, path: SIM_SOCKET_PATH });
      socketRef.current = sim;
      sim.on('connect',       () => {
        if (!active) return;
        setConnected(true);
        setConnectionStatus('fallback');
        setLastConnectedAt(new Date().toISOString());
        setError(null);
      });
      sim.on('disconnect',    () => {
        if (!active) return;
        setConnected(false);
        setConnectionStatus(lastSyncAtRef.current ? 'stale' : 'disconnected');
      });
      sim.on('connect_error', () => {
        if (!active) return;
        setConnected(false);
        setConnectionStatus(lastSyncAtRef.current ? 'stale' : 'disconnected');
        setError('Simulation alert stream unavailable');
      });
      sim.on('alert_new', onAlertNew);
    }

    const core = io(eventsNamespaceUrl(PRIMARY_WS_URL), {
      reconnection:         true,
      reconnectionAttempts: PRIMARY_FAIL_THRESHOLD,
      timeout:              5000,
      path:                 PRIMARY_SOCKET_PATH,
    });
    socketRef.current = core;

    core.on('connect', () => {
      if (!active) return;
      failCount = 0;
      setConnected(true);
      setConnectionStatus('connected');
      setSource('core');
      setLastConnectedAt(new Date().toISOString());
      setError(null);
    });
    core.on('disconnect', () => {
      if (active && !usingFallback) {
        setConnected(false);
        setConnectionStatus(lastSyncAtRef.current ? 'stale' : 'disconnected');
      }
    });
    core.on('connect_error', () => {
      if (!active || usingFallback) return;
      failCount++;
      setConnected(false);
      setConnectionStatus(lastSyncAtRef.current ? 'stale' : 'connecting');
      setError('Live alert feed unavailable');
      if (failCount >= PRIMARY_FAIL_THRESHOLD) connectSimulation();
    });
    core.on('reconnect_failed', () => {
      if (active) setConnectionStatus(lastSyncAtRef.current ? 'stale' : 'disconnected');
      if (!usingFallback) connectSimulation();
    });
    core.on('alert_new',          onAlertNew);
    core.on('alert_state_change', onAlertStateChange);

    // Seed initial state from REST — WS-only mode misses alerts that already exist.
    // Core wraps responses in { data: [...] }; handle both array and wrapped forms.
    // Merge so any WS events that arrived during the fetch take precedence.
    get<Alert[] | AlertListResponse>(alertPath).then(body => {
      if (!active) return;
      const { alerts: existing, summary: responseSummary } = unwrapAlertListResponse(body);
      markSynced(false);
      setSummary(responseSummary);
      if (!existing.length) return;
      setAlerts(prev => {
        const map = new Map<string, Alert>();
        for (const a of existing) map.set(a.id, a);
        for (const a of prev) map.set(a.id, a);        // WS updates win on conflict
        const next = [...map.values()].sort(
          (a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime()
        );
        alertsRef.current = next;
        return next;
      });
    }).catch(() => { /* REST unavailable — stream-only mode */ });

    // Authoritative resync: replaces the local list and summary with the server's view.
    async function resync(): Promise<void> {
      if (!active || !resyncEnabled || resyncInFlight) return;
      resyncInFlight = true;
      const startedAt = Date.now();
      try {
        const body = await get<Alert[] | AlertListResponse>(alertPath);
        if (!active) return;
        // A socket event or lifecycle action applied while this request was in flight is newer than
        // the snapshot; keep it and let the next tick reconcile.
        if (lastLocalChangeAtRef.current > startedAt) return;
        const { alerts: fresh, summary: freshSummary } = unwrapAlertListResponse(body);
        const scoped = fresh
          .filter(alert => (!operationId || alert.operationId === operationId)
            && (!zoneIdsRef.current || zoneIdsRef.current.has(alert.zoneId)))
          .sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime());
        alertsRef.current = scoped;
        setAlerts(scoped);
        setSummary(freshSummary);
        markSynced(false);
        // Fresh data is arriving from Core, whichever channel delivered it.
        if (!usingFallback) {
          setConnected(true);
          setConnectionStatus('connected');
          setSource('core');
        }
      } catch {
        // Keep the last good list; surface staleness only when the socket is not carrying updates.
        if (active && !usingFallback && !core.connected) {
          setConnectionStatus(lastSyncAtRef.current ? 'stale' : 'disconnected');
        }
      } finally {
        resyncInFlight = false;
      }
    }
    resyncEnabled = true;
    const resyncTimer = window.setInterval(() => { void resync(); }, ALERT_RESYNC_MS);

    return () => {
      active = false;
      window.clearInterval(resyncTimer);
      window.removeEventListener(CROWD_SIMULATION_RESET_EVENT, onSimulationReset);
      socketRef.current?.disconnect();
    };
  }, [addOrUpdate, enabled, operationId, requireOperation, zoneIdsKey]);

  const acknowledge = useCallback(async (alertId: string) => {
    const updated = unwrapAlertResponse(
      await post<AlertLifecycleResponse>(`/alerts/${encodeURIComponent(alertId)}/acknowledge`, {}),
    );
    if (updated) addOrUpdate(updated);
  }, [addOrUpdate]);

  const escalate = useCallback(async (alertId: string) => {
    const updated = unwrapAlertResponse(
      await post<AlertLifecycleResponse>(
        `/alerts/${encodeURIComponent(alertId)}/escalate`,
        { reason: CROWD_ALERT_ESCALATION_REASON },
      ),
    );
    if (updated) addOrUpdate(updated);
  }, [addOrUpdate]);

  const resolve = useCallback(async (alertId: string, note: string) => {
    const trimmedNote = note.trim();
    const updated = unwrapAlertResponse(
      await post<AlertLifecycleResponse>(
        `/alerts/${encodeURIComponent(alertId)}/resolve`,
        { note: trimmedNote },
      ),
    );
    if (updated) addOrUpdate(updated);
  }, [addOrUpdate]);

  return {
    alerts,
    summary,
    connected,
    connectionStatus,
    source,
    lastConnectedAt,
    lastEventAt,
    lastSyncAt,
    error,
    acknowledge,
    escalate,
    resolve,
  };
}
